import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, Copy, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PaneHeader } from '@/components/settings/pane-header';
import { SaveBar } from '@/components/settings/queues/save-bar';
import { ChatDirectoryVisibilityRow, ClinicVisibilityList, MenuVisibilityList, PermissionMatrix, permissionsSummary } from '@/components/settings/team/permission-matrix';
import { updatePermissionGroup } from '@/lib/api/team';
import { fetchMessagesSettings } from '@/lib/api/settings';

const clone = (value) => JSON.parse(JSON.stringify(value));

function Field({ label, children }) {
  return (
    <div>
      <div className="mb-1.5 text-[11.5px] font-bold text-muted-foreground">{label}</div>
      {children}
    </div>
  );
}

// "Copy permissions from…" — replaces the draft matrix with a sibling group's map.
function CopyFromGroupPopover({ groups, currentId, onCopy }) {
  const [open, setOpen] = useState(false);
  const others = groups.filter((g) => g.id !== currentId);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" disabled={others.length === 0}>
          <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          Copy permissions from…
          <ChevronDown className="ml-1.5 h-3.5 w-3.5" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-1.5">
        {others.map((g) => (
          <button
            key={g.id}
            type="button"
            onClick={() => {
              onCopy(g);
              setOpen(false);
            }}
            className="flex w-full flex-col rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-accent"
          >
            <span className="truncate font-medium">{g.name}</span>
            <span className="truncate text-[11px] text-muted-foreground">
              {permissionsSummary(g.permissions)}
            </span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Single access-group editor: one draft of {name, description, permissions matrix}, an
 * explicit Save (PUT) via the shared SaveBar, and Delete with confirm. Mirrors the queue
 * editor's draft → dirty → save lifecycle.
 */
export function GroupEditorPane({ initialGroup, groups = [], onBack, onSaved, onDelete }) {
  const [draft, setDraft] = useState(() => clone(initialGroup));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  // Tenant's configured WhatsApp clinic numbers — the dynamic vocabulary for the clinic-tab
  // visibility switches (same source the Messages tab row reads). [] until the fetch resolves.
  const [clinics, setClinics] = useState([]);
  const savedRef = useRef(initialGroup);

  useEffect(() => {
    let cancelled = false;
    fetchMessagesSettings()
      .then((s) => {
        if (!cancelled) {
          setClinics(Array.isArray(s?.whatsappNumbers) ? s.whatsappNumbers : []);
        }
      })
      .catch(() => {
        /* no clinics list → the visibility section shows its empty state */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const editDraft = (next) => {
    setDraft(next);
    setDirty(true);
  };

  const handleSave = async () => {
    if (saving) return;
    if (!String(draft.name || '').trim()) {
      toast.error('Group name is required.');
      return;
    }
    setSaving(true);
    try {
      const updated = await updatePermissionGroup({
        groupId: initialGroup.id,
        name: draft.name,
        description: draft.description,
        permissions: draft.permissions,
        hiddenMenuItems: draft.hiddenMenuItems,
        hiddenClinics: draft.hiddenClinics,
        chatDirectoryVisible: draft.chatDirectoryVisible,
      });
      savedRef.current = updated;
      setDraft(clone(updated));
      setDirty(false);
      onSaved?.(updated);
      toast.success('Access group saved — applies on everyone’s next request.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save access group');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setDraft(clone(savedRef.current));
    setDirty(false);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PaneHeader
        crumb={[{ label: 'Access groups', onClick: onBack }, { label: draft.name || 'Untitled group' }]}
        title="Edit access group"
        action={
          <Button type="button" variant="ghost" size="sm" onClick={() => onDelete?.(savedRef.current)}>
            <Trash2 className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            Delete group
          </Button>
        }
      />

      <ScrollArea className="min-h-0 flex-1">
        <div className="mx-auto flex max-w-3xl flex-col gap-5 p-6">
          <div className="flex flex-col gap-3 md:flex-row">
            <div className="flex-1">
              <Field label="Group name">
                <Input
                  value={draft.name || ''}
                  onChange={(e) => editDraft({ ...draft, name: e.target.value })}
                  maxLength={80}
                  className="h-9 text-sm font-semibold"
                  placeholder="e.g. Front desk"
                />
              </Field>
            </div>
            <div className="flex-[2]">
              <Field label="Description">
                <Input
                  value={draft.description || ''}
                  onChange={(e) => editDraft({ ...draft, description: e.target.value })}
                  maxLength={200}
                  className="h-9 text-sm"
                  placeholder="Who this group is for"
                />
              </Field>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-[13px] font-extrabold tracking-wide text-foreground">FUNCTIONS</span>
            <span className="h-px flex-1 bg-border" />
            <CopyFromGroupPopover
              groups={groups}
              currentId={initialGroup.id}
              onCopy={(g) =>
                editDraft({
                  ...draft,
                  permissions: clone(g.permissions || {}),
                  hiddenMenuItems: clone(g.hiddenMenuItems || []),
                  hiddenClinics: clone(g.hiddenClinics || []),
                  chatDirectoryVisible: g.chatDirectoryVisible === true,
                })
              }
            />
          </div>

          <PermissionMatrix
            value={draft.permissions || {}}
            onChange={(updater) => {
              // Functional apply keeps rapid level changes from clobbering each other.
              setDraft((prev) => ({ ...prev, permissions: updater(prev.permissions || {}) }));
              setDirty(true);
            }}
          />
          <p className="text-[11.5px] leading-relaxed text-muted-foreground">
            View lets people open the area read-only; Manage unlocks its actions. Rights add up
            across a person's groups, and admins bypass groups entirely.
          </p>

          <div className="flex items-center gap-3">
            <span className="text-[13px] font-extrabold tracking-wide text-foreground">MENU VISIBILITY</span>
            <span className="h-px flex-1 bg-border" />
          </div>

          <MenuVisibilityList
            value={draft.hiddenMenuItems || []}
            onChange={(updater) => {
              setDraft((prev) => ({ ...prev, hiddenMenuItems: updater(prev.hiddenMenuItems || []) }));
              setDirty(true);
            }}
          />
          <p className="text-[11.5px] leading-relaxed text-muted-foreground">
            Hiding trims this group's sidebar and Settings rail without changing their rights —
            an item without function access stays hidden regardless. Hidden in any of a
            person's groups stays hidden; admins always see everything. If every Settings
            section is hidden, Preferences is kept so Settings is never an empty page.
          </p>

          <div className="flex items-center gap-3">
            <span className="text-[13px] font-extrabold tracking-wide text-foreground">CLINIC TABS</span>
            <span className="h-px flex-1 bg-border" />
          </div>

          <ClinicVisibilityList
            clinics={clinics}
            value={draft.hiddenClinics || []}
            onChange={(updater) => {
              setDraft((prev) => ({ ...prev, hiddenClinics: updater(prev.hiddenClinics || []) }));
              setDirty(true);
            }}
          />
          <p className="text-[11.5px] leading-relaxed text-muted-foreground">
            Hides clinic tabs from the Messages filter row for this group's members. Cosmetic
            only — the “All” tab still shows those threads. Hidden in any of a person's groups
            stays hidden; admins always see every tab.
          </p>

          <div className="flex items-center gap-3">
            <span className="text-[13px] font-extrabold tracking-wide text-foreground">CHAT DIRECTORY</span>
            <span className="h-px flex-1 bg-border" />
          </div>

          <ChatDirectoryVisibilityRow
            value={draft.chatDirectoryVisible === true}
            onChange={(next) => editDraft({ ...draft, chatDirectoryVisible: next })}
          />
          <p className="text-[11.5px] leading-relaxed text-muted-foreground">
            Everyone is hidden from the Chats people picker by default — turning this on lists
            the group's members so colleagues can chat with them or add them to chat groups.
            Visible in any of a person's groups wins; admins are always visible. Existing chats
            keep working either way.
          </p>
        </div>
      </ScrollArea>

      <SaveBar dirty={dirty} saving={saving} onSave={handleSave} onDiscard={handleDiscard} saveLabel="Save group" />
    </div>
  );
}
